import {StyleSheet, TouchableOpacity, View} from 'react-native';
import {useModal} from './modal.hooks';
import Text from '../text';
import {useCallback, useRef, useState} from 'react';
import React from 'react';
import theme from '@style';
import {designToDp} from '@utils';
import {useTranslation} from 'react-i18next';

const {backgroundColor, borderRadiusSize, flex, font, margin, padding} = theme;

const defaultIconStyles = StyleSheet.create({
  errorIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F04438',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const DEFAULT_ERROR_ICON = (
  <View style={defaultIconStyles.errorIcon}>
    <Text color="#FFF" fontSize={28} fontFamily="fontInterBold">
      !
    </Text>
  </View>
);

export interface ConfirmOptions {
  variant?: 'default' | 'error';
  icon?: React.ReactNode;
  duration?: number;
  closeOnClick?: boolean;
}

export function useConfirm(
  okText?: string,
  cancelText?: string,
  options: ConfirmOptions = {},
) {
  const {i18n} = useTranslation();
  const [title, setTitle] = useState<string>();
  const [message, setMessage] = useState<string | React.ReactElement>();
  const [hideCancel, setHideCancel] = useState<boolean>(false);
  const confirmFn = useRef<() => void>();
  const cancelFn = useRef<() => void>();
  let closeModal = () => {};
  const handleConfirm = () => {
    confirmFn.current && confirmFn.current();
    hide();
  };

  const handleCancel = () => {
    cancelFn.current && cancelFn.current();
    hide();
  };

  const styles = StyleSheet.create({
    border: {borderColor: backgroundColor.grey, borderTopWidth: 1},
    borderRight: {borderRightWidth: 1},
    btnH: {
      height: designToDp(44),
    },
    viewW: {
      width: designToDp(270),
    },
  });

  const modalContent = (
    <TouchableOpacity
      activeOpacity={1}
      onPress={options.closeOnClick ? closeModal : undefined}
      style={[flex.col, styles.viewW]}>
      {options.variant === 'error' ? (
        <View style={[flex.center, margin.tbl]}>
          {options.icon ?? DEFAULT_ERROR_ICON}
        </View>
      ) : null}
      <View style={[flex.col, padding.xxl]}>
        <Text style={[font.bold, font.main, font.fl, font.center]}>
          {title}
        </Text>
        {React.isValidElement(message) ? (
          message
        ) : (
          <Text style={[font.accent, font.fm, margin.tbl, font.center]}>
            {message}
          </Text>
        )}
      </View>
      {options.variant === 'error' ? null : (
        <View style={[flex.row, styles.btnH]}>
          {!hideCancel && (
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={handleCancel}
              style={[
                flex.flex1,
                styles.border,
                flex.center,
                styles.borderRight,
              ]}>
              <Text style={[font.accent, font.fm]}>
                {cancelText || i18n.t('label.cancel')}
              </Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={handleConfirm}
            style={[flex.flex1, styles.border, flex.center]}>
            <Text style={[{color: backgroundColor.main}, font.fm]}>
              {okText || i18n.t('label.confirm')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </TouchableOpacity>
  );

  const {show, hide, renderModal} = useModal(modalContent, {
    delayClose: options.duration,
    backDropClose: options.closeOnClick,
    overlayStyle: [
      {padding: 0, borderRadius: borderRadiusSize.m + borderRadiusSize.s},
    ],
  });
  closeModal = hide;

  const handleShow = useCallback(
    (
      confirmTitle: string,
      confirmMessage: string | React.ReactElement,
      confirmCallback?: () => void,
      cancelCallback?: () => void,
      hideCancelBtn?: boolean | false,
    ) => {
      setTitle(confirmTitle);
      setMessage(confirmMessage);
      confirmFn.current = confirmCallback || (() => {});
      cancelFn.current = cancelCallback || (() => {});
      setHideCancel(Boolean(hideCancelBtn));
      show();
    },
    [show],
  );

  return {
    show: handleShow,
    hide,
    renderModal,
  };
}
